/**
 * Factory для створення CrudResource Feature Handlers
 * 
 * Дозволяє кастомізувати handlers перед створенням CrudResourceFeature
 * Кожен handler може бути переопределен або вимкнений
 */

import * as FormButtonHandlers from './handlers/formButtons'
import * as HeaderPageHandlers from './handlers/headerPage'
import * as TableHandlers from './handlers/table'

/**
 * Конфігурація для кастомізації handlers
 */
export interface ICrudResourceHandlersConfig {
    headerPage?: {
        onButtons?: {
            AddButton?: typeof HeaderPageHandlers.AddButton | null | false
        }
        onBeforeInit?: {
            SetTitleFromResource?: typeof HeaderPageHandlers.SetTitleFromResource | null | false
            SetDocumentTitle?: typeof HeaderPageHandlers.SetDocumentTitle | null | false
        }
        onAfterInit?: {
            ProcessHeaderPageButtons?: typeof HeaderPageHandlers.ProcessHeaderPageButtons | null | false
            PushFilterButtonToTitleExtra?: typeof HeaderPageHandlers.PushFilterButtonToTitleExtra | null | false
            PushTableSettingsButtonToTitleExtra?:
                | typeof HeaderPageHandlers.PushTableSettingsButtonToTitleExtra
                | null
                | false
            PushModeListToExtra?: typeof HeaderPageHandlers.PushModeListToExtra | null | false
        }
    }
    formButtons?: {
        ViewButton?: typeof FormButtonHandlers.ViewButton | null | false
        EditButton?: typeof FormButtonHandlers.EditButton | null | false
        SaveButton?: typeof FormButtonHandlers.SaveButton | null | false
    }
    table?: {
        onBeforeInit?: {
            AddDefaultRowKey?: typeof TableHandlers.AddDefaultRowKey | null | false
        }
        onBuildColumns?: {
            AddActionsMenuColumn?: typeof TableHandlers.AddActionsMenuColumn | null | false
            AddIdColumn?: typeof TableHandlers.AddIdColumn | null | false
        }
        onBuildDataSourceItem?: {
            AddActionsMenuItem?: typeof TableHandlers.AddActionsMenuItem | null | false
            TransformItemDataByDefault?: typeof TableHandlers.TransformItemDataByDefault | null | false
        }
    }
}

/**
 * Створює об'єкт event handlers з можливістю кастомізації
 * 
 * @param config - конфігурація для переопределения handlers
 * @returns об'єкт з event handlers
 */
export const createCrudResourceHandlers = (config: ICrudResourceHandlersConfig = {}) => {
    // HeaderPage Button Handlers
    const headerPageButtonHandlers: Record<string, any> = {}
    
    const addButton = config.headerPage?.onButtons?.AddButton ?? HeaderPageHandlers.AddButton
    if (addButton !== null && addButton !== false) {
        headerPageButtonHandlers['AddButton'] = addButton
    }

    // HeaderPage BeforeInit Handlers
    const headerPageBeforeInitHandlers: Record<string, any> = {}
    
    const setTitleFromResource = config.headerPage?.onBeforeInit?.SetTitleFromResource ?? HeaderPageHandlers.SetTitleFromResource
    if (setTitleFromResource !== null && setTitleFromResource !== false) {
        headerPageBeforeInitHandlers['SetTitleFromResource'] = setTitleFromResource
    }
    
    const setDocumentTitle = config.headerPage?.onBeforeInit?.SetDocumentTitle ?? HeaderPageHandlers.SetDocumentTitle
    if (setDocumentTitle !== null && setDocumentTitle !== false) {
        headerPageBeforeInitHandlers['SetDocumentTitle'] = setDocumentTitle
    }
    
    // HeaderPage AfterInit Handlers
    const headerPageAfterInitHandlers: Record<string, any> = {}
    
    const processHeaderPageButtons = config.headerPage?.onAfterInit?.ProcessHeaderPageButtons ?? HeaderPageHandlers.ProcessHeaderPageButtons
    if (processHeaderPageButtons !== null && processHeaderPageButtons !== false) {
        headerPageAfterInitHandlers['ProcessHeaderPageButtons'] = processHeaderPageButtons
    }
    
    const pushFilterButtonToTitleExtra = config.headerPage?.onAfterInit?.PushFilterButtonToTitleExtra ?? HeaderPageHandlers.PushFilterButtonToTitleExtra
    if (pushFilterButtonToTitleExtra !== null && pushFilterButtonToTitleExtra !== false) {
        headerPageAfterInitHandlers['PushFilterButtonToTitleExtra'] = pushFilterButtonToTitleExtra
    }

    const pushTableSettingsButtonToTitleExtra = config.headerPage?.onAfterInit?.PushTableSettingsButtonToTitleExtra ?? HeaderPageHandlers.PushTableSettingsButtonToTitleExtra
    if (pushTableSettingsButtonToTitleExtra !== null && pushTableSettingsButtonToTitleExtra !== false) {
        headerPageAfterInitHandlers['PushTableSettingsButtonToTitleExtra'] = pushTableSettingsButtonToTitleExtra
    }

    const pushModeListToExtra = config.headerPage?.onAfterInit?.PushModeListToExtra ?? HeaderPageHandlers.PushModeListToExtra
    if (pushModeListToExtra !== null && pushModeListToExtra !== false) {
        headerPageAfterInitHandlers['PushModeListToExtra'] = pushModeListToExtra
    }

    // Form Button Handlers
    const formButtonHandlers: Record<string, any> = {}
    
    const viewButton = config.formButtons?.ViewButton ?? FormButtonHandlers.ViewButton
    if (viewButton !== null && viewButton !== false) {
        formButtonHandlers['ViewButton'] = viewButton
    }
    
    const editButton = config.formButtons?.EditButton ?? FormButtonHandlers.EditButton
    if (editButton !== null && editButton !== false) {
        formButtonHandlers['EditButton'] = editButton
    }
    
    const saveButton = config.formButtons?.SaveButton ?? FormButtonHandlers.SaveButton
    if (saveButton !== null && saveButton !== false) {
        formButtonHandlers['SaveButton'] = saveButton
    }

    // Table BeforeInit Handlers
    const tableBeforeInitHandlers: Record<string, any> = {}
    
    const addDefaultRowKey = config.table?.onBeforeInit?.AddDefaultRowKey ?? TableHandlers.AddDefaultRowKey
    if (addDefaultRowKey !== null && addDefaultRowKey !== false) {
        tableBeforeInitHandlers['AddDefaultRowKey'] = addDefaultRowKey
    }

    // crudContext уже сохраняется в:
    // 1. CrudResourceStore.ts -> headerPageContext storages
    // 2. tableModeFeature.ts -> tableProps.storages.crudContext
    // Не нужно перезаписывать его здесь!

    // Table BuildColumns Handlers
    const tableBuildColumnsHandlers: Record<string, any> = {}
    
    const addActionsMenuColumn = config.table?.onBuildColumns?.AddActionsMenuColumn ?? TableHandlers.AddActionsMenuColumn
    if (addActionsMenuColumn !== null && addActionsMenuColumn !== false) {
        tableBuildColumnsHandlers['AddActionsMenuColumn'] = addActionsMenuColumn
    }
    
    const addIdColumn = config.table?.onBuildColumns?.AddIdColumn ?? TableHandlers.AddIdColumn
    if (addIdColumn !== null && addIdColumn !== false) {
        tableBuildColumnsHandlers['AddIdColumn'] = addIdColumn
    }

    // Table BuildDataSourceItem Handlers
    const tableBuildDataSourceItemHandlers: Record<string, any> = {}
    
    const addActionsMenuItem = config.table?.onBuildDataSourceItem?.AddActionsMenuItem ?? TableHandlers.AddActionsMenuItem
    if (addActionsMenuItem !== null && addActionsMenuItem !== false) {
        tableBuildDataSourceItemHandlers['AddActionsMenuItem'] = addActionsMenuItem
    }
    
    const transformItemDataByDefault = config.table?.onBuildDataSourceItem?.TransformItemDataByDefault ?? TableHandlers.TransformItemDataByDefault
    if (transformItemDataByDefault !== null && transformItemDataByDefault !== false) {
        tableBuildDataSourceItemHandlers['TransformItemDataByDefault'] = transformItemDataByDefault
    }

    return {
        headerPage: {
            onButtons: headerPageButtonHandlers,
            onBeforeInit: headerPageBeforeInitHandlers,
            onAfterInit: headerPageAfterInitHandlers,
        },
        formButtons: formButtonHandlers,
        table: {
            onBeforeInit: tableBeforeInitHandlers,
            onBuildColumns: tableBuildColumnsHandlers,
            onBuildDataSourceItem: tableBuildDataSourceItemHandlers,
        },
    }
}
